import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';
import { CategoryChip, formatCurrency, getCategoryMeta } from './assetUtils';

/**
 * AssetNudgeCard — the interception card shown when a booked/being-booked
 * purchase looks like a fixed asset (asset_suggestions row from the backend
 * detection service).
 *
 * Purely presentational: all actions go through the callbacks so the card
 * can be mounted both in the Föreslagna tab on AssetList and inline in the
 * supplier-invoice booking modal (wired separately).
 *
 * Props:
 *   suggestion — { suggestion_id, reason, suggestion, prefill: { name, amount, quantity, category_code }, source_type }
 *   onSetup    — "Sätt upp avskrivning" (accept → guided asset creation)
 *   onExpense  — "Bokför som vanlig kostnad" (dismiss the suggestion)
 *   busy       — disables the buttons while a request is in flight
 */
const AssetNudgeCard = ({ suggestion, onSetup, onExpense, busy = false }) => {
  const [showExplainer, setShowExplainer] = useState(false);

  if (!suggestion) return null;

  const prefill = suggestion.prefill || {};
  const category = prefill.category_code ? getCategoryMeta(prefill.category_code) : null;
  const amount = parseFloat(prefill.amount);
  const quantity = parseInt(prefill.quantity, 10) || 1;

  return (
    <div
      className="modern-card"
      style={{
                marginBottom: '1rem',
        overflow: 'visible',
      }}
    >
      <div style={{ padding: '1.25rem 1.5rem' }}>
        <div className="d-flex align-items-start gap-2">
          <div
            style={{
              flexShrink: 0,
              width: 34,
              height: 34,
              borderRadius: 10,
              background: 'var(--rgba-primary-1)',
              color: 'var(--primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Lightbulb size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: 'var(--title)', fontSize: '0.9375rem' }}>
              Det här ser ut som en tillgång
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--secondary)', marginTop: 2 }}>
              {suggestion.reason}
            </div>
            {(prefill.name || !isNaN(amount)) && (
              <div
                className="d-flex align-items-center gap-2 flex-wrap"
                style={{ marginTop: 8, fontSize: '0.8125rem', color: 'var(--title)' }}
              >
                {category && <CategoryChip code={category.code} size={24} iconSize={13} tone="neutral" />}
                {prefill.name && <span style={{ fontWeight: 600 }}>{prefill.name}</span>}
                {quantity > 1 && <span style={{ color: 'var(--secondary)' }}>× {quantity}</span>}
                {!isNaN(amount) && (
                  <span style={{ color: 'var(--secondary)' }}>{formatCurrency(amount)} exkl. moms</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap" style={{ marginTop: 14 }}>
          <Button variant="primary" size="sm" disabled={busy} onClick={() => onSetup && onSetup(suggestion)}>
            Sätt upp avskrivning
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={busy}
            onClick={() => onExpense && onExpense(suggestion)}
          >
            Bokför som vanlig kostnad
          </Button>
          <Button
            variant="link"
            size="sm"
            style={{ textDecoration: 'none', color: 'var(--primary)', padding: '0.25rem 0.5rem' }}
            onClick={() => setShowExplainer((prev) => !prev)}
          >
            Vad betyder det här?{' '}
            {showExplainer ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Button>
        </div>

        {showExplainer && (
          <div
            style={{
              marginTop: 12,
              padding: '0.75rem 1rem',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              fontSize: '0.8125rem',
              color: 'var(--secondary)',
              lineHeight: 1.55,
            }}
          >
            En tillgång är något företaget köper och använder i flera år — som en dator
            eller bil. Istället för att dra av hela kostnaden direkt fördelas den över
            åren, det kallas avskrivning. Väljer du <strong>Sätt upp avskrivning</strong>{' '}
            bokför LedgerLoop en del av kostnaden automatiskt varje månad. Väljer du{' '}
            <strong>Bokför som vanlig kostnad</strong> dras hela beloppet av direkt.
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetNudgeCard;
